'use client'

import { useClerk } from '@clerk/nextjs'
import { useEffect } from 'react'

import {
  CLERK_LOCALIZATIONS,
  type ClerkLocalization,
} from '@/lib/i18n/clerk-localization-loaders'
import { readerLocale } from '@/lib/i18n/reader-locale'

/** The part of clerk-js `ClerkProvider` itself uses to apply a new `localization`. */
type ClerkPropsUpdater = {
  __internal_updateProps?: (props: {
    options: { localization: ClerkLocalization }
  }) => Promise<unknown> | undefined
}

/**
 * Clerk's own UI in the reader's language inside the product roots (the
 * account and security modal, organization dialogs, sign-in from a template
 * page). Their prerendered document is the same in every language, so
 * `ClerkProvider` starts in English; this loads the reader's translation in
 * the browser and hands it to clerk-js the way `ClerkProvider` applies a
 * changed `localization` prop (clerk-js merges it into its options). English
 * readers load nothing.
 */
export function ProductClerkLocalization() {
  const clerk = useClerk()
  useEffect(() => {
    const locale = readerLocale()
    // English is Clerk's built-in default.
    if (locale === 'en') {
      return
    }
    let current = true
    CLERK_LOCALIZATIONS[locale]()
      .then((localization) => {
        if (current) {
          return (clerk as ClerkPropsUpdater).__internal_updateProps?.({
            options: { localization },
          })
        }
      })
      // Clerk keeps English: a missed translation never breaks its UI.
      .catch(() => {})
    return () => {
      current = false
    }
  }, [clerk])
  return null
}
